/**
 * useCompanyContext Hook
 * Diz aos formulários se o campo "empresa" precisa ser perguntado — decisão V2 da spec #408.
 *
 * A empresa ativa é a do manifesto: opera-se uma empresa por vez, trocando no cabeçalho, e o
 * formulário não pergunta o que já foi escolhido. Na clínica e na plataforma não há empresa
 * ativa — lá o campo continua aparecendo, porque na clínica a empresa é filtro e na
 * plataforma não há recorte.
 */

import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useManifestoStore } from '../stores/manifestoStore';

export interface CompanyContextResult {
  /** Whether the company field should be displayed in forms */
  shouldShowCompanyField: boolean;
  /** The effective company ID to use (the active context, when it is a company) */
  effectiveCompanyId: number | null;
  /** The effective company ID as a string (for form fields) */
  effectiveCompanyIdString: string;
  /** Array of company IDs the user has access to */
  boundCompanyIds: number[];
  /** Whether the user can create global resources (the active context is the platform) */
  canCreateGlobalResource: boolean;
}

/**
 * Hook that provides company context information for form fields
 *
 * @example
 * ```tsx
 * const { shouldShowCompanyField, effectiveCompanyIdString } = useCompanyContext();
 *
 * // Auto-fill company when hidden
 * useEffect(() => {
 *   if (!shouldShowCompanyField && effectiveCompanyIdString && !watch('companyId')) {
 *     setValue('companyId', effectiveCompanyIdString);
 *   }
 * }, [shouldShowCompanyField, effectiveCompanyIdString]);
 * ```
 */
export function useCompanyContext(): CompanyContextResult {
  const { user } = useAuthStore();
  const manifesto = useManifestoStore((estado) => estado.manifesto);

  return useMemo(() => {
    const boundCompanyIds = user?.tenantContext?.companyIds ?? [];

    // Sem manifesto nenhuma tela protegida chega a renderizar; se chegar, perguntar a empresa
    // é o caminho seguro — adivinhá-la criaria o registro na empresa errada.
    const contextoAtivo = manifesto?.contextoAtivo;
    const effectiveCompanyId =
      contextoAtivo?.tipo === 'empresa' ? contextoAtivo.id ?? null : null;

    const shouldShowCompanyField = effectiveCompanyId === null;

    // Recurso global é o que não pende de empresa nem de clínica: só o contexto de
    // Plataforma não tem recorte, e é lá que criá-lo faz sentido.
    const canCreateGlobalResource = contextoAtivo?.tipo === 'plataforma';

    return {
      shouldShowCompanyField,
      effectiveCompanyId,
      effectiveCompanyIdString: effectiveCompanyId?.toString() ?? '',
      boundCompanyIds,
      canCreateGlobalResource,
    };
  }, [user, manifesto]);
}

export default useCompanyContext;
